import { ExternalAccount } from "./account";
import { Unionize } from "./generic";
import { UserModule } from "./modules";
import { PermissionLevel } from "./permission";

export const UserRoles = {
  guest: 0,
  member: 1,
  admin: 2,
} as const;
export const UserRole = ["guest", "member", "admin"] as const;
export type UserRole = Unionize<typeof UserRole>;

export type UserBase = { ID: string; Email: string };

export type UserBaseExtended = UserBase & { ExemptLicense: boolean };

export type User = UserBase & {
  Rank: number | null;
};

/**
 * This is a representation of how users are retrieved from superuser only routes.
 */
export type SuperuserUser = User & {
  ExemptLicense: boolean;
  Superuser: boolean;
};

/**
 * The current user's own user object.
 * This includes properties omitted from other user objects
 */
export type OwnUser = User & {
  Modules: UserModule[];
};

export type Invitation = {
  ID: string;
  AccountID: string;
  CreatedAt: string;
  ExpireTime: string;
  DeletedBy: string | null;
  Role: UserRole;
  InvitationStatus: string;
  Creator: UserBase;
  User: UserBase;
};

/**
 * When a user is invited to an account they do not have access to get the account name
 * so we include the account name in the invitation object.
 */
export type AccountInvitation = Invitation & {
  Account: ExternalAccount;
};

export type AccountBase = {
  ID: string;
  Name: string;
};

export type UserShareSuggestion = {
  Type: "UserShare";
  User: UserBase;
  PermissionLimit: null | {
    Reason: string;
    Level: PermissionLevel;
  };
};

export type AccountShareSuggestion = {
  Type: "AccountShare";
} & AccountBase;

type ShareSuggestionType = "Usershare" | "AccountShare";

export type ShareSuggestion = UserShareSuggestion | AccountShareSuggestion;

export type UserMentionSuggestion = {
  Type: "UserMention";
  User: UserBase;
  Shared: boolean;
};

export enum UserPreferenceKey {
  UI_TOUR = "ui_tours",
  TOS_CHANGE = "terms_of_service_changes",
  GRID_STATE = "grid_state",
  ACCOUNT_NAVIGATION = "account_navigation",
}

export enum UserPreferenceSubKey {
  /**
   * A tour for new users when they first view a project
   */
  PROJECT_INTRO = "intro_tour_active",
  /**
   * A tour for existing users as of FOND version 1.5 explaining
   * the change of layout (first release of the dynamic layout system)
   */
  DYNAMIC_LAYOUT = "dynamic_layouts_tour_active",
  /**
   * A tour for the introduction of style editor feature to users
   */
  STYLE_EDITOR = "style_editor_tour_active",
  /**
   * A notification of terms of service changes in May 2023.
   */
  MAY_2023_TOS_CHANGE = "2023-05_modal_active",
  /**
   * A notification of terms of self-serve uplift service changes in Sept 2025.
   */
  SEP_2025_TOS_CHANGE = "2025-09_ss_modal_active",
  /**
   * A tour for existing users introducing cost to serve feature
   * and major UI update on the auto design panel
   */
  COST_TO_SERVE_STACKED_NAVIGATION = "cost_to_serve_stack_navigation_active",
  /**
   * A tour for existing users introducing financial analytics
   */
  FINANCIAL_ANALYTICS_INTRO = "report_tour_active",
  /**
   * A tour containing link to the guide to generate report with an old project
   */
  FINANCIAL_ANALYTICS_OUTDATED_PROJECT = "report_outdated_project_tour_active",
  /**
   * A tour for self service features and other released features.
   */
  SELF_SERVICE_TOUR = "pricing_tour_active",

  PINNED_ACCOUNTS = "pinned_accounts",

  STARRED_PROJECTS = "starred_projects",

  RECENT_PROJECTS = "recent_projects",
}

export type UserPreference = {
  Key: UserPreferenceKey;
  Subkey: string;
  ID: string;
  Value: any;
  UserID: string | null;
};

/**
 * Customised planner features. This is used to apply customised architecture labels,
 * descriptions and rules to particular accounts.
 *
 * This can also be extended when we have more features, e.g:
 *    const FlexNapFeature = "FlexNap"
 *    const FooFeature = "Foo"
 *    type PlannerFeatures = typeof FlexNapFeature | typeof Foo
 */
export const FlexNapFeature = "FlexNap";
export type PlannerFeatures = typeof FlexNapFeature;
